import {Component, Input, OnInit} from '@angular/core';
import {
    IncidentMsisdnFraudEntriesResponse,
    IncidentResponse,
    MsisdnFraudEntryApiService,
    ReferenceBookResponse,
    ReferenceBookType,
} from '@npck/af-frontend-api';
import {NESTED_TAB, RouterService, rxSpinner} from '@npck/libs/commons';
import {DialogService} from '@npck/libs/ui-kit';
import {combineLatest, filter, map, mergeMap, of} from 'rxjs';
import {FilterField} from '../../../../components/full-table/filter/filter.component';
import {initialTableEvent, mergeTableEvent, TableEvent} from '../../../../components/full-table/full-table.component';
import {
    applyLargeEllipsis,
    Column,
    ColumnType,
    CustomLayoutColumn,
    Row,
    StatusColumn,
} from '../../../../components/full-table/table/table.component';
import {mapMsisdnStatusToStyle} from '../../../../components/status/status.component';
import {AuthHolderService} from '../../../../domain/auth-holder.service';
import {DictionaryHolderService, resolveRefName} from '../../../../domain/dictionary-holder.service';
import {IncidentDetailsFilterHolderService} from '../../../../domain/incident-details-filter-holder.service';
import {formatMsisdnDisplay} from '../../../../domain/phone-utils';
import {INCIDENT_ID, MSISDN_FRAUD_ENTRY_ID} from '../../../../domain/query-params.keys';
import {Path} from '../../../../path';

const SPINNER_ID = 'msisdnFraudEntries';
const MAX_ENTRIES = 50;

@Component({
    selector: 'af-msisdn-fraud-entries-tab',
    standalone: false,
    templateUrl: './msisdn-fraud-entries-tab.component.html',
})
export class MsisdnFraudEntriesTabComponent implements OnInit {
    @Input() incident: IncidentResponse;
    hasEditAccess = false;
    hasDeleteAccess = false;
    filterFields: FilterField[] = [
        {
            type: 'text',
            name: 'query',
        },
    ];
    dataSource: Row[] = [];
    tableEvent: TableEvent;
    totalElementsForFilter: number;
    totalElements = 0;
    MAX_ENTRIES = MAX_ENTRIES;

    constructor(
        private msisdnFraudEntryApiService: MsisdnFraudEntryApiService,
        private dictionaryHolderService: DictionaryHolderService,
        private filterHolderService: IncidentDetailsFilterHolderService,
        private authHolderService: AuthHolderService,
        private dialogService: DialogService,
        private routerService: RouterService,
    ) {
    }

    get canAddEntry(): boolean {
        return this.hasEditAccess && this.totalElements < MAX_ENTRIES;
    }

    get canDeleteEntry(): boolean {
        return this.hasDeleteAccess && this.totalElements > 1;
    }

    ngOnInit(): void {
        this.hasEditAccess = !this.incident.isDeleted
            && (this.incident.is_author || this.authHolderService.hasAccess(['npck-admin']));
        this.hasDeleteAccess = !this.incident.isDeleted && this.authHolderService.hasAccess(['npck-admin']);
        this.loadMsisdnFraudEntries(
            this.filterHolderService.getState(this.incident.id, 'tabMsisdnFraudEntries')?.tableEvent
                ?? this.buildInitialTableEvent(),
        );
    }

    loadMsisdnFraudEntries(tableEvent: TableEvent): void {
        const prevEvent = this.filterHolderService.getState(this.incident.id, 'tabMsisdnFraudEntries')?.tableEvent;
        this.tableEvent = mergeTableEvent(prevEvent, tableEvent);
        this.filterHolderService.storeTableState(this.incident.id, 'tabMsisdnFraudEntries', this.tableEvent);
        const totalEntries$ = this.tableEvent.filter.query
            ? this.msisdnFraudEntryApiService.getIncidentMsisdnFraudEntries(this.incident.id, {limit: 1, offset: 0})
                .pipe(map(response => response.total))
            : of(null);
        combineLatest([
            this.dictionaryHolderService.getReference(ReferenceBookType.MOBILE_OPERATOR),
            this.dictionaryHolderService.getReference(ReferenceBookType.MSISDN_STATUS),
            this.dictionaryHolderService.getReference(ReferenceBookType.MOBILE_OPERATOR_EVENT_SOURCE),
            this.dictionaryHolderService.getReference(ReferenceBookType.SUBSCRIBER_CATEGORY),
            this.dictionaryHolderService.getReference(ReferenceBookType.COUNTRY),
            this.msisdnFraudEntryApiService.getIncidentMsisdnFraudEntries(this.incident.id, {
                searchQuery: this.tableEvent.filter.query,
                limit: this.tableEvent.page.pageSize,
                offset: this.tableEvent.page.pageOffset,
                sortBy: this.tableEvent.sortBy?.columnName,
                sortOrder: this.tableEvent.sortBy?.direction,
            }),
            totalEntries$,
        ])
            .pipe(rxSpinner(this.tableEvent.spinnerId))
            .subscribe(([operators, statuses, reasons, categories, countries, response, totalEntries]) => {
                this.totalElements = totalEntries ?? response.total;
                this.buildDataSource(response, operators, statuses, reasons, categories, countries);
            });
    }

    navigateToCreateEntry(): void {
        this.navigateToEntry(Path.INCIDENTS_MSISDN_FRAUD_ENTRY_CREATE);
    }

    deleteEntry(entryId: string, msisdn: string): void {
        this.dialogService.openDialog({
            title: 'i18n_af_msisdnFraudEntryTabDeletionDialogTitle',
            subtitle: 'i18n_af_msisdnFraudEntryTabDeletionDialogSubtitle',
            buttonOkText: 'i18n_af_buttonDelete',
            buttonCancelText: 'i18n_af_buttonCancel',
            isButtonOkAccent: true,
            translationParams: {msisdn},
        })
            .pipe(
                filter(response => !!response),
                mergeMap(() => this.msisdnFraudEntryApiService.deleteMsisdnFraudEntry(this.incident.id, entryId)),
                rxSpinner(SPINNER_ID),
            )
            .subscribe(() => this.loadMsisdnFraudEntries(this.tableEvent));
    }

    private navigateToEntry(path: string, entryId?: string): void {
        const pathParams: Record<string, string> = {[INCIDENT_ID]: this.incident.id};
        if (entryId) {
            pathParams[MSISDN_FRAUD_ENTRY_ID] = entryId;
        }
        this.routerService.navigate(path, {
            pathParams,
            queryParams: {[NESTED_TAB]: 'tabMsisdnFraudEntries'},
            queryParamsHandling: 'merge',
        });
    }

    private buildInitialTableEvent(): TableEvent {
        return {
            ...initialTableEvent,
            spinnerId: SPINNER_ID,
            filter: {
                query: '',
            },
            sortBy: {
                columnName: 'status',
                direction: 'asc',
            },
        };
    }

    private buildDataSource(
        response: IncidentMsisdnFraudEntriesResponse,
        operators: ReferenceBookResponse[],
        statuses: ReferenceBookResponse[],
        reasons: ReferenceBookResponse[],
        categories: ReferenceBookResponse[],
        countries: ReferenceBookResponse[],
    ): void {
        this.totalElementsForFilter = response.total;
        this.dataSource = response.items.map(item => {
            const fullName = [item.lastName, item.firstName, item.middleName].filter(part => !!part).join(' ');
            const subscriberMsisdns = (item.subscriberMsisdns ?? []).map(formatMsisdnDisplay).join(', ');
            const msisdn = formatMsisdnDisplay(item.msisdn);
            return <Row>{
                ...(this.hasEditAccess ? {
                    button: <CustomLayoutColumn>{
                        type: ColumnType.CUSTOM_LAYOUT,
                        cellStyle: {
                            layoutDirection: 'row',
                            layoutAlignFirst: 'start',
                            layoutAlignSecond: 'center',
                            layoutGap: '8px',
                        },
                        layoutContentItems: [
                            {
                                type: 'icon',
                                text: 'ic_edit',
                                click: () => this.navigateToEntry(Path.INCIDENTS_MSISDN_FRAUD_ENTRY_EDIT, item.id),
                                tooltip: 'i18n_af_labelEdit',
                                iconSize: 'i-16',
                            },
                            ...(this.canDeleteEntry ? [{
                                type: 'icon',
                                text: 'ic_delete',
                                click: () => this.deleteEntry(item.id, msisdn),
                                tooltip: 'i18n_af_labelDeactivate',
                                iconSize: 'i-16',
                                iconColor: 'c-secondary-red-800',
                            }] : []),
                        ],
                    },
                } : {}),
                msisdn: <Column>{
                    type: ColumnType.TEXT,
                    text: msisdn,
                },
                operator: <Column>{
                    type: ColumnType.TEXT,
                    text: resolveRefName(operators, item.operator),
                },
                status: <StatusColumn>{
                    type: ColumnType.STATUS,
                    sortable: true,
                    text: resolveRefName(statuses, item.status),
                    style: mapMsisdnStatusToStyle(item.status),
                },
                activationDate: <Column>{
                    type: ColumnType.DATE,
                    sortable: true,
                    text: item.activationDate,
                },
                terminationDate: <Column>{
                    type: ColumnType.DATE,
                    sortable: true,
                    text: item.terminationDate,
                },
                contactDate: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    text: item.contactDate,
                },
                reason: <Column>{
                    type: ColumnType.TEXT,
                    text: resolveRefName(reasons, item.reason),
                },
                category: <Column>{
                    type: ColumnType.TEXT,
                    text: resolveRefName(categories, item.category),
                },
                isResident: <Column>{
                    type: ColumnType.TEXT,
                    text: this.resolveResidency(item.isResident),
                },
                iin: <Column>{
                    type: ColumnType.TEXT,
                    text: item.iin ?? item.passportNumber,
                },
                fullName: <Column>{
                    type: ColumnType.TEXT,
                    text: fullName,
                    ...applyLargeEllipsis(fullName),
                },
                country: <Column>{
                    type: ColumnType.TEXT,
                    text: resolveRefName(countries, item.country),
                },
                subscriberMsisdns: <Column>{
                    type: ColumnType.TEXT,
                    text: subscriberMsisdns,
                    ...applyLargeEllipsis(subscriberMsisdns),
                },
            };
        });
    }

    private resolveResidency(isResident: boolean): string {
        if (isResident === null || isResident === undefined) {
            return '';
        }
        return isResident ? 'i18n_af_residentStatus#resident' : 'i18n_af_residentStatus#nonResident';
    }
}
